import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { useCorporateStatsFilters, useEmStatsFilters } from '@/creditflowresearch/application/PageFilters/hooks';
import getPrefixForTitle from '@/creditflowresearch/ui/common/helpers/getPrefixForTitle';
import getMarketPrefixForTitle from '@/creditflowresearch/ui/common/helpers/getMarketPrefixForTitle';
import getProductPrefixForTitle from '@/creditflowresearch/ui/common/helpers/getProductPrefixForTitle';
import getEmRegionPrefixForTitle from '@/creditflowresearch/ui/common/helpers/getEmRegionPrefixForTitle';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { getMarketGradient } from '@/creditflowresearch/ui/common/utils/markets/marketStyleHelpers';
import absColumns from '@/creditflowresearch/ui/components/creditFlow/Stats/columns';
import corporateColumns from '@/creditflowresearch/ui/components/creditFlow/Stats/corporateStatscolumns';
import emStatscolumns from '@/creditflowresearch/ui/components/creditFlow/Stats/emStatscolumns';
import { IFilterSet } from '@/creditflowresearch/ui/common/hooks/filters/useFilterButtons';
import Product, { CorporateProduct } from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import Market, { EmergingMarket } from '@/creditflowresearch/domain/Market';
import { IColumn } from '@creditflowresearch/uilib/Table/DataTable/interfaces/Column';
import { IMarketCategory } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketCategory';

export type CategoryFilter = ({
  category,
  productIds,
  regionIds,
  marketIds,
}: {
  category: IMarketCategory,
  productIds: string[],
  regionIds: string[],
  marketIds: string[],
}) => boolean;

export interface IStatsConfig {
  headerFilters: IFilterSet[],
  titlePrefix: string,
  title?: string,
  panelBackground: string,
  columns: IColumn[],
  filterCategory: CategoryFilter,
}

export const useAbsStatsConfig = (): IStatsConfig => {
  const { query: { regions, products } } = useRouter();
  const { regionFilters, corporateProductFilters, includedProductIds } = usePageFiltersContext();
  const panelBackground = useProductGradient();

  const regionIds = Region.getIds(regions);
  const productIds = Product.getIds(products);

  return {
    headerFilters: [
      { key: 'regions', items: regionFilters },
      {
        key: 'products',
        items: corporateProductFilters,
        isVisible: Product.areAllOfTypes(includedProductIds, CorporateProduct.CORPORATE_TYPES),
      },
    ],
    titlePrefix: getPrefixForTitle(regionIds, productIds, true),
    panelBackground,
    columns: absColumns,
    filterCategory: ({ category, productIds, regionIds }) =>
      productIds.includes(category.productId) && regionIds.includes(category.regionId),
  };
};

export const useCorporateMarketStatsConfig = (): IStatsConfig => {
  const { query: { markets, products } } = useRouter();
  const corporateMarketFilters = useCorporateStatsFilters();
  const panelBackground = useProductGradient();

  const marketIds = Market.getIds(markets);
  const productIds = Product.getIds(products);

  const titlePrefix = useMemo(
    () => getMarketPrefixForTitle(marketIds) + getProductPrefixForTitle(productIds),
    [marketIds, productIds],
  );

  return {
    headerFilters: corporateMarketFilters,
    titlePrefix: titlePrefix,
    panelBackground,
    columns: corporateColumns,
    filterCategory: ({ category, productIds, marketIds }) =>
      productIds.includes(category.productId) && marketIds.includes(category.marketId),
  };
};

export const useEmergingMarketStatsConfig = (): IStatsConfig => {
  const { query: { regions } } = useRouter();
  const emStatsFilters = useEmStatsFilters();

  const titlePrefix = useMemo(
    () => getEmRegionPrefixForTitle(Region.getIds(regions)),
    [regions],
  );

  return {
    headerFilters: emStatsFilters,
    titlePrefix: titlePrefix,
    title: 'EM Stats',
    panelBackground: getMarketGradient(EmergingMarket.ABBREVIATION),
    columns: emStatscolumns,
    filterCategory: ({ category, regionIds, marketIds }) =>
      regionIds.includes(category.regionId) && marketIds.includes(category.marketId),
  };
};
